/* import __COLOCATED_TEMPLATE__ from './discount-codes.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import type { DiscountCode } from './types';

interface Signature {
  Args: {
    shopifyDomain: string | null;
    appId: string;
    conversationId: number;
    onDisableSuccess?: () => void;
  };
}

export default class DiscountCodes extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare session: $TSFixMe;

  @tracked discountCodes: DiscountCode[] = [];
  @tracked errorMessage: string | null = null;
  @tracked showDisableModal = false;
  @tracked selectedDiscountCode: DiscountCode | null = null;
  @tracked isExpanded = false;
  @tracked hasFetched = false;

  get isLoading(): boolean {
    return taskFor(this.fetchDiscountCodesTask).isRunning;
  }

  get isFeatureEnabled(): boolean {
    return this.session.workspace.isFeatureEnabled('tdf-shopify-disable-discount-code');
  }

  get hasDiscountCodes(): boolean {
    return this.discountCodes.length > 0;
  }

  @action
  loadDiscountCodes() {
    if (this.isFeatureEnabled && this.args.shopifyDomain) {
      taskFor(this.fetchDiscountCodesTask).perform();
    }
  }

  @action
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded && !this.hasFetched && !this.isLoading) {
      this.loadDiscountCodes();
    }
  }

  @action
  openDisableModal(discountCode: DiscountCode) {
    this.selectedDiscountCode = discountCode;
    this.showDisableModal = true;
  }

  @action
  closeDisableModal() {
    this.showDisableModal = false;
    this.selectedDiscountCode = null;
  }

  @action
  onDisableSuccess() {
    this.closeDisableModal();
    taskFor(this.fetchDiscountCodesTask).perform();
    this.args.onDisableSuccess?.();
  }

  formatValue(discountCode: DiscountCode): string {
    let value = Math.abs(parseFloat(discountCode.value));
    if (discountCode.value_type === 'percentage') {
      return `${value}%`;
    }
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: discountCode.currency_code || 'USD',
      }).format(value);
    } catch {
      return `$${value}`;
    }
  }

  @task({ restartable: true })
  *fetchDiscountCodesTask(): TaskGenerator<void> {
    try {
      this.errorMessage = null;
      let response: { discount_codes: DiscountCode[] } = yield ajax({
        url: '/ember/inbox/shopify/discount_codes',
        type: 'GET',
        data: {
          app_id: this.args.appId,
          shopify_domain: this.args.shopifyDomain,
        },
      });
      this.discountCodes = response.discount_codes;
      this.hasFetched = true;
    } catch (error: unknown) {
      captureException(error);
      this.errorMessage = this.intl.t(
        'inbox.conversation-details-sidebar.shopify.discount-codes.error',
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::DiscountCodes': typeof DiscountCodes;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/discount-codes': typeof DiscountCodes;
  }
}
